/**
 * Boolean expression parser for ADI schema conditional logic.
 *
 * Grammar (from types.json#rule_engine):
 *   expr       = or_expr
 *   or_expr    = and_expr ('OR' and_expr)*
 *   and_expr   = not_expr ('AND' not_expr)*
 *   not_expr   = 'NOT' not_expr | comparison
 *   comparison = atom (op atom)?
 *   op         = '==' | '!=' | '>' | '<' | '>=' | '<=' | 'IN' | 'NOT_IN' | 'MATCHES'
 *   atom       = string_literal | number_literal | list_literal | function_call | field_ref
 *   function_call = 'TODAY()' | 'YEARS_BETWEEN(a,b)' | 'IS_EMPTY(f)' | 'COUNT(f)'
 *   field_ref  = identifier ('.' identifier | '[*]')*
 *   string_literal  = '"' chars '"'
 *   number_literal  = [0-9]+ ('.' [0-9]+)?
 *   list_literal    = '[' (atom (',' atom)*)? ']'
 *
 * The parser produces an AST that the evaluator walks.
 */

// ---------------------------------------------------------------------------
// AST node types
// ---------------------------------------------------------------------------

export type AstNode =
  | BinaryOpNode
  | NotNode
  | ComparisonNode
  | FieldRefNode
  | StringLiteralNode
  | NumberLiteralNode
  | ListLiteralNode
  | FunctionCallNode;

export interface BinaryOpNode {
  kind: 'BinaryOp';
  op: 'AND' | 'OR';
  left: AstNode;
  right: AstNode;
}

export interface NotNode {
  kind: 'Not';
  operand: AstNode;
}

export interface ComparisonNode {
  kind: 'Comparison';
  op: '==' | '!=' | '>' | '<' | '>=' | '<=' | 'IN' | 'NOT_IN' | 'MATCHES';
  left: AstNode;
  right: AstNode;
}

export interface FieldRefNode {
  kind: 'FieldRef';
  path: string;
}

export interface StringLiteralNode {
  kind: 'StringLiteral';
  value: string;
}

export interface NumberLiteralNode {
  kind: 'NumberLiteral';
  value: number;
}

export interface ListLiteralNode {
  kind: 'ListLiteral';
  items: AstNode[];
}

export interface FunctionCallNode {
  kind: 'FunctionCall';
  name: 'TODAY' | 'YEARS_BETWEEN' | 'IS_EMPTY' | 'COUNT';
  args: AstNode[];
}

// ---------------------------------------------------------------------------
// Tokeniser
// ---------------------------------------------------------------------------

type TokenKind =
  | 'STRING'
  | 'NUMBER'
  | 'IDENT'
  | 'LPAREN'
  | 'RPAREN'
  | 'LBRACKET'
  | 'RBRACKET'
  | 'COMMA'
  | 'DOT'
  | 'STAR'
  | 'EQ'
  | 'NEQ'
  | 'GTE'
  | 'LTE'
  | 'GT'
  | 'LT'
  | 'EOF';

interface Token {
  kind: TokenKind;
  value: string;
}

function tokenise(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    // Skip whitespace
    if (/\s/.test(input[i])) {
      i++;
      continue;
    }

    // Double-quoted string
    if (input[i] === '"') {
      let j = i + 1;
      while (j < input.length && input[j] !== '"') {
        if (input[j] === '\\') j++; // escape
        j++;
      }
      tokens.push({ kind: 'STRING', value: input.slice(i + 1, j) });
      i = j + 1;
      continue;
    }

    // Number (integer or decimal)
    if (/[0-9]/.test(input[i])) {
      let j = i;
      while (j < input.length && /[0-9.]/.test(input[j])) j++;
      tokens.push({ kind: 'NUMBER', value: input.slice(i, j) });
      i = j;
      continue;
    }

    // Two-char operators
    if (input.slice(i, i + 2) === '==') { tokens.push({ kind: 'EQ', value: '==' }); i += 2; continue; }
    if (input.slice(i, i + 2) === '!=') { tokens.push({ kind: 'NEQ', value: '!=' }); i += 2; continue; }
    if (input.slice(i, i + 2) === '>=') { tokens.push({ kind: 'GTE', value: '>=' }); i += 2; continue; }
    if (input.slice(i, i + 2) === '<=') { tokens.push({ kind: 'LTE', value: '<=' }); i += 2; continue; }
    if (input.slice(i, i + 3) === '[*]') { tokens.push({ kind: 'IDENT', value: '[*]' }); i += 3; continue; }

    // Single-char tokens
    switch (input[i]) {
      case '>': tokens.push({ kind: 'GT', value: '>' }); i++; continue;
      case '<': tokens.push({ kind: 'LT', value: '<' }); i++; continue;
      case '(': tokens.push({ kind: 'LPAREN', value: '(' }); i++; continue;
      case ')': tokens.push({ kind: 'RPAREN', value: ')' }); i++; continue;
      case '[': tokens.push({ kind: 'LBRACKET', value: '[' }); i++; continue;
      case ']': tokens.push({ kind: 'RBRACKET', value: ']' }); i++; continue;
      case ',': tokens.push({ kind: 'COMMA', value: ',' }); i++; continue;
      case '.': tokens.push({ kind: 'DOT', value: '.' }); i++; continue;
      case '*': tokens.push({ kind: 'STAR', value: '*' }); i++; continue;
    }

    // Identifier or keyword (AND, OR, NOT, IN, NOT_IN, MATCHES, function names)
    if (/[a-zA-Z_]/.test(input[i])) {
      let j = i;
      while (j < input.length && /[a-zA-Z0-9_]/.test(input[j])) j++;
      tokens.push({ kind: 'IDENT', value: input.slice(i, j) });
      i = j;
      continue;
    }

    // Unknown character — skip with warning
    console.warn(`[schema-renderer/parser] Unknown character '${input[i]}' at position ${i}`);
    i++;
  }

  tokens.push({ kind: 'EOF', value: '' });
  return tokens;
}

// ---------------------------------------------------------------------------
// Recursive-descent parser
// ---------------------------------------------------------------------------

class Parser {
  private tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    return this.tokens[this.pos++];
  }

  private match(kind: TokenKind, value?: string): boolean {
    const t = this.peek();
    if (t.kind !== kind) return false;
    if (value !== undefined && t.value !== value) return false;
    return true;
  }

  private expect(kind: TokenKind, value?: string): Token {
    const t = this.peek();
    if (t.kind !== kind || (value !== undefined && t.value !== value)) {
      throw new Error(
        `[schema-renderer/parser] Expected ${kind}${value ? `="${value}"` : ''} but got ${t.kind}="${t.value}" at position ${this.pos}`,
      );
    }
    return this.consume();
  }

  parse(): AstNode {
    const node = this.parseOr();
    if (this.peek().kind !== 'EOF') {
      // Remaining tokens — not a hard error, just a warning
      console.warn('[schema-renderer/parser] Unexpected tokens after expression end');
    }
    return node;
  }

  private parseOr(): AstNode {
    let left = this.parseAnd();
    while (this.match('IDENT', 'OR')) {
      this.consume();
      const right = this.parseAnd();
      left = { kind: 'BinaryOp', op: 'OR', left, right } as BinaryOpNode;
    }
    return left;
  }

  private parseAnd(): AstNode {
    let left = this.parseNot();
    while (this.match('IDENT', 'AND')) {
      this.consume();
      const right = this.parseNot();
      left = { kind: 'BinaryOp', op: 'AND', left, right } as BinaryOpNode;
    }
    return left;
  }

  private parseNot(): AstNode {
    if (this.match('IDENT', 'NOT')) {
      this.consume();
      // Handle NOT_IN as a special case — it may have been tokenised as IDENT("NOT")
      // followed by IDENT("IN") — merge them into a comparison op later.
      // But bare NOT (logical negation) applies to the next expression.
      const operand = this.parseNot();
      return { kind: 'Not', operand } as NotNode;
    }
    return this.parseComparison();
  }

  private parseComparison(): AstNode {
    const left = this.parseAtom();

    const t = this.peek();
    let op: ComparisonNode['op'] | null = null;

    if (t.kind === 'EQ') op = '==';
    else if (t.kind === 'NEQ') op = '!=';
    else if (t.kind === 'GTE') op = '>=';
    else if (t.kind === 'LTE') op = '<=';
    else if (t.kind === 'GT') op = '>';
    else if (t.kind === 'LT') op = '<';
    else if (t.kind === 'IDENT' && t.value === 'IN') op = 'IN';
    else if (t.kind === 'IDENT' && t.value === 'NOT_IN') op = 'NOT_IN';
    else if (t.kind === 'IDENT' && t.value === 'MATCHES') op = 'MATCHES';
    // Handle NOT IN (two tokens)
    else if (t.kind === 'IDENT' && t.value === 'NOT') {
      const saved = this.pos;
      this.consume(); // consume NOT
      if (this.match('IDENT', 'IN')) {
        op = 'NOT_IN';
        this.consume(); // consume IN
        const right = this.parseAtom();
        return { kind: 'Comparison', op: 'NOT_IN', left, right } as ComparisonNode;
      }
      // Not a NOT IN — push back
      this.pos = saved;
    }

    if (op !== null) {
      this.consume(); // consume the operator token
      const right = this.parseAtom();
      return { kind: 'Comparison', op, left, right } as ComparisonNode;
    }

    return left;
  }

  private parseAtom(): AstNode {
    const t = this.peek();

    // String literal
    if (t.kind === 'STRING') {
      this.consume();
      return { kind: 'StringLiteral', value: t.value } as StringLiteralNode;
    }

    // Number literal
    if (t.kind === 'NUMBER') {
      this.consume();
      return { kind: 'NumberLiteral', value: parseFloat(t.value) } as NumberLiteralNode;
    }

    // List literal: [ atom, atom, ... ]
    if (t.kind === 'LBRACKET') {
      this.consume(); // [
      const items: AstNode[] = [];
      while (!this.match('RBRACKET') && this.peek().kind !== 'EOF') {
        items.push(this.parseAtom());
        if (this.match('COMMA')) this.consume();
      }
      this.expect('RBRACKET');
      return { kind: 'ListLiteral', items } as ListLiteralNode;
    }

    // Parenthesised expression
    if (t.kind === 'LPAREN') {
      this.consume();
      const inner = this.parseOr();
      this.expect('RPAREN');
      return inner;
    }

    // Identifier: could be a keyword function call or a field reference
    if (t.kind === 'IDENT') {
      const name = t.value;

      // Function calls
      if (name === 'TODAY') {
        this.consume();
        this.expect('LPAREN');
        this.expect('RPAREN');
        return { kind: 'FunctionCall', name: 'TODAY', args: [] } as FunctionCallNode;
      }
      if (name === 'YEARS_BETWEEN' || name === 'IS_EMPTY' || name === 'COUNT') {
        this.consume();
        this.expect('LPAREN');
        const args: AstNode[] = [];
        while (!this.match('RPAREN') && this.peek().kind !== 'EOF') {
          args.push(this.parseAtom());
          if (this.match('COMMA')) this.consume();
        }
        this.expect('RPAREN');
        return {
          kind: 'FunctionCall',
          name: name as FunctionCallNode['name'],
          args,
        } as FunctionCallNode;
      }

      // Field reference: build dot-notation path including [*] segments
      this.consume();
      let path = name;
      while (this.match('DOT') || (this.peek().kind === 'IDENT' && this.peek().value === '[*]')) {
        if (this.match('DOT')) {
          this.consume();
          const seg = this.expect('IDENT');
          path += '.' + seg.value;
        } else {
          // [*] was tokenised as a single IDENT token
          const seg = this.consume();
          path += seg.value;
        }
      }
      return { kind: 'FieldRef', path } as FieldRefNode;
    }

    throw new Error(
      `[schema-renderer/parser] Unexpected token ${t.kind}="${t.value}" at position ${this.pos}`,
    );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse a boolean expression string into an AST.
 * Returns null (and logs a warning) if the expression is empty or null.
 * Returns null on parse errors to allow graceful degradation.
 */
export function parseExpression(expr: string | null | undefined): AstNode | null {
  if (!expr || expr.trim() === '') return null;
  try {
    const tokens = tokenise(expr.trim());
    const parser = new Parser(tokens);
    return parser.parse();
  } catch (err) {
    console.warn('[schema-renderer/parser] Parse error:', err, '\nExpression:', expr);
    return null;
  }
}
